const HISTORY_DATE_KEYS = Object.freeze([
  "Date",
  "GameDate",
  "Game Date",
  "GAME_DATE",
  "DateStr",
  "game_date",
  "date",
]);

const HISTORY_ID_KEYS = Object.freeze([
  "GAME_ID",
  "Game ID",
  "GameID",
  "game_id",
  "Event ID",
  "event_id",
]);

const INCOMPLETE_STATUS = /scheduled|pre[_ -]?game|pending|postponed|cancel(?:led|ed)|suspended|upcoming|future/i;

const utcDate = (year, month, day) => {
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day
    ? parsed
    : null;
};

export function historyDateValue(row) {
  if (row instanceof Date || typeof row === "string" || typeof row === "number") return row;
  for (const key of HISTORY_DATE_KEYS) {
    const value = row?.[key];
    if (value !== null && value !== undefined && String(value).trim()) return value;
  }
  return null;
}

/** Parse provider history dates without accepting JavaScript's rollover dates. */
export function parseHistoryDate(input) {
  const raw = historyDateValue(input);
  if (raw instanceof Date) return Number.isFinite(raw.getTime()) ? new Date(raw.getTime()) : null;
  if (typeof raw === "number") {
    const parsed = new Date(raw);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  const text = String(raw ?? "").trim();
  if (!text) return null;

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (iso) {
    const day = utcDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
    if (!day) return null;
    if (text.includes("T") && !Number.isFinite(Date.parse(text))) return null;
    return day;
  }

  const compact = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return compact ? utcDate(Number(compact[3]), Number(compact[1]), Number(compact[2])) : null;
}

const statusIsComplete = (row) => {
  const status = row?.Status ?? row?.status ?? row?.GameStatus ?? row?.game_status;
  return !status || !INCOMPLETE_STATUS.test(String(status));
};

const explicitGameId = (row) => {
  for (const key of HISTORY_ID_KEYS) {
    const value = row?.[key];
    if (value !== null && value !== undefined && String(value).trim()) return `${key}:${String(value).trim()}`;
  }
  return "";
};

const historyIdentity = ({ row, timestamp, value }) => {
  const gameId = explicitGameId(row);
  if (gameId) return gameId;
  const opponent = row?.Opponent ?? row?.["Opponent Name"] ?? row?.Opp ?? "";
  const matchup = row?.Matchup ?? row?.matchup ?? "";
  return `${timestamp}|${String(matchup).trim().toUpperCase()}|${String(opponent).trim().toUpperCase()}|${value}`;
};

/**
 * Return valid completed history with values attached. Selection is newest-first,
 * while the chart-facing default order is chronological (oldest to newest).
 */
export function selectCompletedHistory(rows, {
  getValue = (row) => row?.value,
  limit = 5,
  now = new Date(),
  order = "oldest-first",
} = {}) {
  const nowDate = parseHistoryDate(now) || (now instanceof Date ? now : new Date(now));
  const futureCutoff = Number.isFinite(nowDate?.getTime()) ? nowDate.getTime() : Date.now();
  const candidates = (Array.isArray(rows) ? rows : [])
    .map((row, sourceIndex) => {
      const date = parseHistoryDate(row);
      const rawValue = getValue(row);
      const value = rawValue === null || rawValue === undefined || rawValue === ""
        ? null
        : Number(rawValue);
      return { row, sourceIndex, date, timestamp: date?.getTime(), value };
    })
    .filter((game) => Number.isFinite(game.timestamp)
      && game.timestamp <= futureCutoff
      && Number.isFinite(game.value)
      && statusIsComplete(game.row))
    .sort((a, b) => b.timestamp - a.timestamp || a.sourceIndex - b.sourceIndex);

  const seen = new Set();
  const selected = [];
  for (const game of candidates) {
    const identity = historyIdentity(game);
    if (seen.has(identity)) continue;
    seen.add(identity);
    selected.push(game);
    if (selected.length >= Math.max(0, limit)) break;
  }
  return order === "newest-first" ? selected : selected.slice().reverse();
}

/**
 * Some line variants omit logs even though another projection for the same
 * player carries the player's real game-log rows. Choose the most complete
 * existing source; never synthesize a game or combine different players.
 */
export function bestAvailableCompletedHistory(primaryRow, projectionPool, {
  getRows,
  getValue,
  now = new Date(),
  limit = 100,
} = {}) {
  if (typeof getRows !== "function" || typeof getValue !== "function") return [];
  const primaryPlayerId = primaryRow?.["Player ID"] ?? primaryRow?.PLAYER_ID ?? primaryRow?.PlayerID;
  const primaryPlayer = String(primaryRow?.Player ?? primaryRow?.player ?? "").trim().toUpperCase();
  const primarySport = String(primaryRow?.Sport ?? primaryRow?.sport ?? "").trim().toUpperCase();
  const matchesPlayer = (row) => {
    const candidateId = row?.["Player ID"] ?? row?.PLAYER_ID ?? row?.PlayerID;
    if (primaryPlayerId !== null && primaryPlayerId !== undefined && candidateId !== null && candidateId !== undefined) {
      return String(candidateId) === String(primaryPlayerId);
    }
    return primaryPlayer && String(row?.Player ?? row?.player ?? "").trim().toUpperCase() === primaryPlayer;
  };
  const candidates = [
    primaryRow,
    ...(Array.isArray(projectionPool) ? projectionPool : []).filter((row) => row !== primaryRow
      && matchesPlayer(row)
      && (!primarySport || !row?.Sport || String(row.Sport).trim().toUpperCase() === primarySport)),
  ].filter(Boolean);

  return candidates.reduce((best, row) => {
    const games = selectCompletedHistory(getRows(row), {
      getValue,
      now,
      limit,
      order: "newest-first",
    });
    return games.length > best.length ? games : best;
  }, []);
}

const seasonStartMonth = (sport) => {
  const key = String(sport || "").trim().toUpperCase();
  if (["NBA", "NHL", "NCAAB"].includes(key)) return 10;
  if (["SOCCER"].includes(key)) return 7;
  return 1;
};

export function sportSeasonForDate(input, sport) {
  const date = parseHistoryDate(input);
  if (!date) return null;
  const year = date.getUTCFullYear();
  return date.getUTCMonth() + 1 < seasonStartMonth(sport) ? year - 1 : year;
}

const seasonName = (season, sport) => {
  if (!Number.isInteger(season)) return "";
  return seasonStartMonth(sport) === 1 ? String(season) : `${season}\u2013${String(season + 1).slice(-2)}`;
};

/** Shared date-label and prior-season rules for every sport's primary chart. */
export function historyDatePresentation(games, { sport, referenceDate = new Date() } = {}) {
  const dates = (Array.isArray(games) ? games : [])
    .map((game) => game?.date || parseHistoryDate(game?.row ?? game))
    .filter((date) => Number.isFinite(date?.getTime()));
  const years = new Set(dates.map((date) => date.getUTCFullYear()));
  const seasons = [...new Set(dates.map((date) => sportSeasonForDate(date, sport)).filter(Number.isInteger))].sort();
  const referenceSeason = sportSeasonForDate(referenceDate, sport);
  const entirelyPrior = seasons.length > 0
    && Number.isInteger(referenceSeason)
    && seasons.every((season) => season < referenceSeason);
  const includeYear = years.size > 1 || seasons.length > 1 || entirelyPrior;
  const seasonLabel = entirelyPrior
    ? (seasons.length === 1
      ? `${seasonName(seasons[0], sport)} season`
      : `${seasonName(seasons[0], sport)}\u2013${seasonName(seasons.at(-1), sport)} seasons`)
    : "";
  return { includeYear, seasonLabel, years, seasons, referenceSeason };
}

export function formatHistoryDate(input, { includeYear = false, fallback = "\u2014" } = {}) {
  const date = input?.date || parseHistoryDate(input?.row ?? input);
  if (!date) return fallback;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

export function formatHistoricalTableDate(input) {
  return formatHistoryDate(input, { includeYear: true });
}
